'use client'

import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

/**
 * One section of a vCRO page: a card with a heading (an <h2>, so the page
 * reads as an outline to a screen reader), an optional sentence under it and
 * an optional control at the inline end.
 *
 * `stackActionWhenNarrow`: a wide control (the 3 / 6 / 12 months buttons)
 * beside the heading leaves the heading a column a few words wide on a phone.
 * On a card narrower than 28rem it goes on its own row under the heading
 * instead. The card is the container the query measures (a header cannot
 * query itself), and only such a card is one: `container-type` would collapse
 * a card whose width came from its content.
 */
export function VcroCard({
  title,
  description,
  action,
  stackActionWhenNarrow = false,
  children,
  className,
  contentClassName,
  testId,
  labelledBy,
}: {
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  stackActionWhenNarrow?: boolean
  children: ReactNode
  className?: string
  contentClassName?: string
  testId?: string
  /** The id the heading carries, for a region that names itself by it. */
  labelledBy?: string
}) {
  const stack = Boolean(action) && stackActionWhenNarrow
  return (
    <Card
      className={cn('h-full min-w-0 gap-3', stack && '@container/vcro-card', className)}
      data-testid={testId}
      aria-labelledby={labelledBy}
      role={labelledBy ? 'region' : undefined}
    >
      {/* `!`: the header's own has-[card-action] column rule is more specific than a container query. */}
      <CardHeader className={cn('gap-1', stack && '@max-md/vcro-card:grid-cols-1!')}>
        <CardTitle>
          <h2 id={labelledBy} className="text-sm font-semibold leading-tight">
            {title}
          </h2>
        </CardTitle>
        {description ? <CardDescription className="text-xs leading-relaxed">{description}</CardDescription> : null}
        {action ? (
          <CardAction
            className={cn(
              'flex items-center gap-2',
              stack &&
                '@max-md/vcro-card:col-start-1! @max-md/vcro-card:row-span-1! @max-md/vcro-card:row-start-3! @max-md/vcro-card:justify-self-start! @max-md/vcro-card:mt-1',
            )}
          >
            {action}
          </CardAction>
        ) : null}
      </CardHeader>
      <CardContent className={cn('min-w-0', contentClassName)}>{children}</CardContent>
    </Card>
  )
}
